import {
  StoreScheduleCreateRequest,
  StoreScheduleResponse,
  ValidationProblemCode,
  ValidationProblemResponse,
} from '@ydistri/api-sdk';
import { fieldErrors, messageFor } from '../../Configuration/Validator/problemsLib';

export interface DateRange {
  from: string | null;
  to: string | null;
}

const EMPTY_RANGE: DateRange = { from: null, to: null };

export interface StoreScheduleForm {
  storeId: number | undefined;
  cannotSend: boolean;
  cannotReceive: boolean;
  /** Only ever consulted while both restrictions are ticked; kept so re-ticking restores it. */
  differentPeriods: boolean;
  send: DateRange;
  receive: DateRange;
  note: string;
}

export const EMPTY_FORM: StoreScheduleForm = {
  storeId: undefined,
  cannotSend: false,
  cannotReceive: false,
  differentPeriods: false,
  send: EMPTY_RANGE,
  receive: EMPTY_RANGE,
  note: '',
};

const rangeIsSet = (range: DateRange): boolean => !!range.from && !!range.to;

const sameRange = (left: DateRange, right: DateRange): boolean =>
  left.from === right.from && left.to === right.to;

/** Two ranges are shown only while both restrictions are ticked and they were asked to differ. */
export const showsTwoRanges = (form: StoreScheduleForm): boolean =>
  form.cannotSend && form.cannotReceive && form.differentPeriods;

/** The offer to differentiate makes no sense with a single restriction. */
export const offersDifferentPeriods = (form: StoreScheduleForm): boolean =>
  form.cannotSend && form.cannotReceive;

/**
 * With one range on screen it is the receive range, except when only sending is restricted.
 * Which one it is decides what an edit round trip restores.
 */
export const singleRangeSide = (form: StoreScheduleForm): 'send' | 'receive' =>
  form.cannotReceive ? 'receive' : 'send';

/**
 * What each restriction resolves to once the single-range collapse is applied. A restriction that
 * is not ticked has no period at all - that is what "not restricted" is stored as.
 */
export const effectiveRanges = (
  form: StoreScheduleForm,
): { send: DateRange; receive: DateRange } => {
  const single = form[singleRangeSide(form)];
  const twoRanges = showsTwoRanges(form);

  const resolve = (restricted: boolean, own: DateRange): DateRange => {
    if (!restricted) return EMPTY_RANGE;
    if (twoRanges) return own;
    return single;
  };

  return {
    send: resolve(form.cannotSend, form.send),
    receive: resolve(form.cannotReceive, form.receive),
  };
};

/**
 * Reads the modal's state back out of a stored record. The three shapes a record can have are
 * both pairs equal, both pairs different, and one pair only.
 */
export const deriveFormState = (record: StoreScheduleResponse): StoreScheduleForm => {
  const send: DateRange = { from: record.cannotSendFrom ?? null, to: record.cannotSendTo ?? null };
  const receive: DateRange = {
    from: record.cannotReceiveFrom ?? null,
    to: record.cannotReceiveTo ?? null,
  };
  const cannotSend = rangeIsSet(send);
  const cannotReceive = rangeIsSet(receive);

  return {
    storeId: record.store.id,
    cannotSend,
    cannotReceive,
    differentPeriods: cannotSend && cannotReceive && !sameRange(send, receive),
    send,
    receive,
    note: record.note ?? '',
  };
};

export const buildPayload = (form: StoreScheduleForm): StoreScheduleCreateRequest => {
  if (form.storeId === undefined) {
    throw new Error('buildPayload requires a form that validate() has accepted.');
  }

  const { send, receive } = effectiveRanges(form);

  // Nulls are sent rather than omitted so the payload states the whole record, not a delta.
  return {
    storeId: form.storeId,
    cannotSendFrom: send.from,
    cannotSendTo: send.to,
    cannotReceiveFrom: receive.from,
    cannotReceiveTo: receive.to,
    note: form.note.trim() === '' ? null : form.note.trim(),
  };
};

export interface FormErrors {
  store?: string;
  restrictions?: string;
  send?: string;
  receive?: string;
  /** The whole record's period has passed: judged once, on the later of the two end dates. */
  period?: string;
}

// Local copies of the server's rules: same codes, so a refusal from the server reads the same.
const validateRange = (range: DateRange): string | undefined => {
  if (!range.from || !range.to)
    return messageFor(ValidationProblemCode.StoreSchedulePeriodIncomplete);
  if (range.from > range.to) return messageFor(ValidationProblemCode.StoreSchedulePeriodReversed);
  return undefined;
};

/** The record's last date - the later end date of the ticked restrictions - as the server reads it. */
const lastDate = (form: StoreScheduleForm): string | null => {
  const { send, receive } = effectiveRanges(form);
  const ends = [form.cannotSend ? send.to : null, form.cannotReceive ? receive.to : null].filter(
    (d): d is string => d !== null,
  );
  return ends.length === 0 ? null : ends.reduce((a, b) => (a > b ? a : b));
};

/**
 * The backend's date, carried by every register record. An empty register carries none, so only then
 * the browser's date stands in; the backend re-checks it on write either way.
 */
export const registerDate = (
  records: StoreScheduleResponse[] | undefined,
  browserToday: string,
): string => records?.[0]?.evaluatedOn ?? browserToday;

export const validate = (form: StoreScheduleForm, today: string, isCreate: boolean): FormErrors => {
  const errors: FormErrors = {};

  if (form.storeId === undefined) {
    errors.store = messageFor(ValidationProblemCode.StoreScheduleStoreRequired);
  }

  if (!form.cannotSend && !form.cannotReceive) {
    errors.restrictions = messageFor(ValidationProblemCode.StoreScheduleRestrictionRequired);
    return errors;
  }

  const { send, receive } = effectiveRanges(form);
  if (form.cannotSend) {
    errors.send = validateRange(send);
  }
  if (form.cannotReceive) {
    errors.receive = validateRange(receive);
  }

  // A create is refused only when the whole record is in the past - one period ended and the other
  // still ahead passes, as on the server.
  const last = lastDate(form);
  if (isCreate && last !== null && last < today) {
    errors.period = messageFor(ValidationProblemCode.StoreSchedulePeriodEnded);
  }

  return errors;
};

export const hasErrors = (errors: FormErrors): boolean =>
  Object.values(errors).some(message => message !== undefined);

export interface ServerAnswer {
  fields: FormErrors;
  /** Problems on the whole record (the period already ended, no restriction) and anything unmapped. */
  other: string[];
}

export const NO_SERVER_ANSWER: ServerAnswer = { fields: {}, other: [] };

const fieldOfPath: Record<string, keyof FormErrors> = {
  storeId: 'store',
  cannotSendFrom: 'send',
  cannotSendTo: 'send',
  cannotReceiveFrom: 'receive',
  cannotReceiveTo: 'receive',
};

/**
 * A refusal from the server sorted onto the form's fields; the restriction rule and the ended-period
 * rule report on the root and land where the form shows them.
 */
export const serverAnswer = (problems: ValidationProblemResponse[]): ServerAnswer => {
  const onRoot: Partial<Record<ValidationProblemCode, keyof FormErrors>> = {
    [ValidationProblemCode.StoreScheduleRestrictionRequired]: 'restrictions',
    [ValidationProblemCode.StoreSchedulePeriodEnded]: 'period',
  };
  const sorted = fieldErrors(
    problems.filter(p => onRoot[p.code] === undefined),
    fieldOfPath,
  );
  const fields: FormErrors = {};
  sorted.fields.forEach(f => {
    fields[f.name] = f.errors.join(' ');
  });
  problems.forEach(p => {
    const field = onRoot[p.code];
    if (field !== undefined) fields[field] = [fields[field], p.message].filter(Boolean).join(' ');
  });
  return { fields, other: sorted.other };
};

/**
 * The record competes with the store's other active records only once it would itself be active,
 * which is what an end date still ahead of today means.
 */
export const wouldBeActive = (form: StoreScheduleForm, today: string): boolean => {
  const { send, receive } = effectiveRanges(form);
  return [send, receive].some(range => rangeIsSet(range) && range.to !== null && range.to >= today);
};

export const findActiveSiblings = (
  form: StoreScheduleForm,
  today: string,
  all: StoreScheduleResponse[],
  editedStoreScheduleId?: number,
): StoreScheduleResponse[] => {
  if (form.storeId === undefined || !wouldBeActive(form, today)) return [];

  return all.filter(
    candidate =>
      candidate.isActive &&
      candidate.store.id === form.storeId &&
      candidate.storeScheduleId !== editedStoreScheduleId,
  );
};
